import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationLogout, logout } from '@/store/slices/authentication';
import { fetchUserPreferences, selectNShowcase } from '@/store/slices/userPreferences';
import {
  blogFetch,
  blogPollArticleJobs,
  blogPollTenantJobs,
  blogStartArticleGeneration,
  pollWhileVisible,
  isBrandChanged,
  JOB_POLL_MS,
  JOB_WAIT_LIMIT_MS,
  blogInsufficientCredits,
  blogNeededCredits,
  blogFetchNotices,
} from '@/store/slices/blog';
import { fetchOwnerWallets } from '@/store/slices/wallet';
import { BuyCreditsSheet } from '../../pages/creatives/components/BuyCreditsSheet';
import { isAdvancedVideoEditor } from '../../pages/creatives/video-routes';
import { useGameListsWarmup } from '../../pages/creatives/hooks/useGameListsWarmup';
import { clearNewArticles, isBlogPath } from '../../pages/blog/newArticles';
import { settleAutopilotTopup, useAutopilotCreditsSettle } from '../../pages/blog/autopilotTopup';

import { useAuthGate } from '../useAuthGate';
import { FtuxRootLazy } from '../../features/ftux/layer/FtuxRootLazy';
import { selectFtuxBusy, selectFtuxMapOn } from '../../features/ftux/store/selectors';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { usePointerGlow } from '../../hooks/usePointerGlow';
import { useCreativeJobsStream } from '../../hooks/useCreativeJobsStream';
import { ROUTE } from '../../routes';
import { LUMIO_DRAWER_RANGE, LUMIO_MOBILE_ANIM, MOBILE_MQ } from '../../theme/tokens';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { SideMenu } from './components/SideMenu';
import { TopBar } from './components/TopBar';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { LumioChat } from './components/LumioChat';
import { LumioDrawer } from './components/LumioDrawer';
import { LumioMobile } from './components/LumioMobile';
import { MobileNav } from './components/MobileNav';
import { useOwnerNotificationsSync } from './useOwnerNotificationsSync';
import { LumioControlContext, type LumioControl, type LumioSeed } from './lumioControl';
import { useLumioNudges } from './useLumioNudges';
import { LumioNudgeContext } from './lumioNudge';
import { NudgeToast, renderNudge } from './components/NudgeToast/NudgeToast';
import { isDockableNudge } from './components/LumioChat/briefing';
import { ImmersiveControlContext, type ImmersiveControl, type TitleEditor } from './immersiveControl';
import { useTranslation } from 'react-i18next';
import { LumioArticleContext, type LumioArticleControl, type LumioArticleResult } from './lumioArticle';
import {
  Root,
  SideMenuCol,
  Body,
  Content,
  LumioCol,
  MobileBodyScroll,
  StudioBackdrop,
  ImmersiveLayout,
} from './MainLayout.styles';

export type MainLayoutProps = {
  children: React.ReactNode;
};

/**
 * Hand-written chrome for the nova fixed routes — converted from the
 * remote-config "Page Layout" template (side menu · top bar · content card ·
 * Lumio chat · mobile nav). Replaces the `FixedConfigPage` bridge: the layout
 * no longer depends on `config.setPages`, and it owns the auth gate the config
 * pages used to provide (not authenticated → /login).
 */
export const MainLayout = ({ children }: MainLayoutProps): React.ReactElement | null => {
  // Shared auth gate (also used by CustomizerLayout) — see ../useAuthGate.
  // `authenticated` is kept as the content-rect effect dependency below.
  const { authenticated, gate } = useAuthGate();

  // Cursor-following hover glow for every clickable card (theme/mixins.hoverWash):
  // one document-level controller writes the pointer position into the hovered
  // card's glow variables. Mounted here so the whole studio shares a single
  // listener regardless of which page is on screen.
  usePointerGlow();

  // Keep the creative-generation jobs live + durable app-wide: rehydrate the owner's jobs on load; progress then
  // arrives over the owner-hub websocket, with a stall / tab-return re-read as the safety net (see the hook).
  // Mounted here (not on the Creatives page) so a run keeps tracking, and lands its result in the tray, across
  // route changes and reloads.
  useCreativeJobsStream();
  useGameListsWarmup();
  // The owner's notifications (the top bar's bell), read on load and on a tab return; new ones
  // arrive on the websocket.
  useOwnerNotificationsSync();

  // Load the owner's saved preferences (profile / billing / comms / lastBrandId)
  // once per authenticated session — app-wide, so they're available on a direct
  // load of any page, not just via the `/` root redirect. Idempotent: the
  // loaded/loading guard means a second mount (or the root redirect's own fetch)
  // never refires it. The brand reconciliation listener in `store/index.ts` waits
  // on this to settle.
  const dispatch = useAppDispatch();
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const prefsLoading = useAppSelector((state) => state.userPreferences.loading);

  // Support impersonation: when an admin is signed in AS this account, /auth/check
  // returns the agent under `admin` (see store/slices/authentication). While set, a
  // notice strip is pinned to the top of the viewport (above all chrome) and its
  // measured height is published as `--ows-imp-offset` so the chrome offsets itself
  // by that amount, pushing the studio down instead of overlapping it.
  const impersonator = useAppSelector((state) => state.authentication.user.admin);
  // The Newton showcase shows no owner wallet: the phone's More drawer leaves its Wallet row out.
  const newtonShowcase = useAppSelector(selectNShowcase);
  // FTUX - Getting Started (features/ftux): its layer mounts while the `ftux` flag is on (every owner since
  // 2026-10-22) and nobody is signed in as the owner. While it holds the screen (Lumio's hello, the map, a piece's
  // pop-up, a piece in play) Lumio's nudges wait: none is asked for and the toast stays away.
  const ftuxOn = useFeatureGate().isEnabled('ftux');
  const navigate = useNavigate();
  // The Creatives pages (Website Creatives, Game Lists, Marketing Assets) are their own Lumio-assisted
  // surface, so the studio-wide docked Lumio panel (and the header trigger that opens it as a drawer) are
  // hidden there to avoid two Lumios. Blog, the other Website tab, keeps the panel.
  const location = useLocation();
  const ftuxBusy = useAppSelector((state) => selectFtuxBusy(state, location.pathname === ROUTE.home()));
  // Lumio is available on every page (the Creatives pages included).
  const { t } = useTranslation();
  // Opt-in full-screen mode: a page (the Advanced Video editor) can slide the side menu out and own the
  // screen. State lives here; the page toggles it through ImmersiveControlContext, and the layout shows a
  // back button that exits (running the page's onExit). OFF by default → nothing changes for any other page.
  const [immersive, setImmersiveState] = React.useState(false);
  // An optional name editor the immersive page (the video ad editor) renders in the top bar's breadcrumb slot.
  const [titleEditor, setTitleEditor] = React.useState<TitleEditor | null>(null);
  const immersiveExitRef = React.useRef<(() => void) | null>(null);
  const setImmersive = React.useCallback((on: boolean, onExit?: () => void): void => {
    setImmersiveState(on);
    immersiveExitRef.current = on ? onExit ?? null : null;
  }, []);
  const handleImmersiveBack = React.useCallback((): void => {
    const onExit = immersiveExitRef.current;
    immersiveExitRef.current = null;
    setImmersiveState(false);
    setTitleEditor(null);
    onExit?.();
  }, []);
  const immersiveControl = React.useMemo<ImmersiveControl>(
    () => ({ immersive, setImmersive, exitImmersive: handleImmersiveBack, titleEditor, setTitleEditor }),
    [immersive, setImmersive, handleImmersiveBack, titleEditor],
  );
  // Auto-exit immersive on any route change: immersive mutates the SHARED `.pg-layout`, so a flag left on
  // during navigation would strip the gutters / content card from the INCOMING page through the page
  // transition. Drop it whenever the path changes. `immersive` is read at navigation time only and is
  // deliberately NOT a dep: adding it would re-run this effect the instant a page turns immersive on and
  // immediately cancel it.
  //
  // EXCEPTION: the video editor is itself a set of immersive ROUTES (/videos/new, /videos/:id). Entering one
  // is now a navigation, and the editor page's useLayoutEffect sets immersive DURING that commit — so by the
  // time this passive effect flushes, its closure already sees immersive=true and would clobber the page's
  // own request, leaving the page immersed but the layout expanded. Skip the reset when the DESTINATION is an
  // immersive editor route (the page owns immersive there); still reset when leaving to any normal route.
  React.useEffect(() => {
    // Only the ADVANCED video editor (`?mode=advanced`) owns immersive; the easy modal (default) does not.
    const destinationOwnsImmersive = isAdvancedVideoEditor(location.pathname, location.search);
    if (immersive && !destinationOwnsImmersive) {
      setImmersiveState(false);
      immersiveExitRef.current = null;
      setTitleEditor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  // The blog list's "New" badges (`pages/blog/newArticles`, the Autopilot notice the owner followed
  // from Home) live only while the owner stays inside the blog area: the list, the editor, the
  // preview. This layout is the one surface mounted across every route, so it is where leaving the
  // area is noticed, on every path change. Arriving IN the area (Home's "See articles" writes the
  // ids, then navigates) is not a change that clears.
  React.useEffect(() => {
    if (!isBlogPath(location.pathname)) clearNewArticles();
  }, [location.pathname]);
  const [signingOut, setSigningOut] = React.useState(false);
  // End the support session. `authenticationLogout` revokes only THIS device's tokens
  // (access + refresh, the impersonation key) — the owner's sessions on other devices
  // are untouched — then we clear local auth and leave for /login.
  const handleImpersonationSignOut = React.useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    await dispatch(authenticationLogout());
    dispatch(logout());
    navigate('/login');
  }, [signingOut, dispatch, navigate]);
  // Callback ref so the offset is (re)published exactly when the strip's DOM node
  // attaches or detaches — independent of render/auth-gate ordering. A ResizeObserver
  // keeps it in lock-step as the strip wraps to two lines on narrow phones / on resize.
  const bannerObserver = React.useRef<ResizeObserver | null>(null);
  const setBannerNode = React.useCallback((node: HTMLButtonElement | null) => {
    const root = document.documentElement;
    bannerObserver.current?.disconnect();
    bannerObserver.current = null;
    if (!node) {
      // No strip → no offset; the chrome's `var(--ows-imp-offset, 0px)` falls back to 0
      // and the mobile top bar keeps its own notch inset.
      root.style.removeProperty('--ows-imp-offset');
      root.style.removeProperty('--ows-imp-safe-top');
      return;
    }
    // The strip owns the notch inset (its mobile top padding includes the safe area),
    // so tell the mobile top bar to drop its own (otherwise the inset is double-counted).
    root.style.setProperty('--ows-imp-safe-top', '0px');
    const publish = (): void => {
      if (!node.isConnected) return;
      root.style.setProperty('--ows-imp-offset', `${node.getBoundingClientRect().height}px`);
    };
    publish();
    // Re-measure after layout settles and after webfonts load: the copy wraps to a
    // second line on narrow phones once the real font swaps in, growing the strip —
    // the chrome offset must follow or the header tucks under it. (ResizeObserver
    // catches later changes; these cover the first paint where it can be measured short.)
    requestAnimationFrame(publish);
    void document.fonts?.ready.then(publish).catch(() => undefined);
    const ro = new ResizeObserver(publish);
    ro.observe(node);
    bannerObserver.current = ro;
  }, []);
  React.useEffect(() => {
    if (authenticated && !prefsLoaded && !prefsLoading) {
      dispatch(fetchUserPreferences());
    }
  }, [authenticated, prefsLoaded, prefsLoading, dispatch]);

  // (Saved-language adoption lives app-wide in `OwsThemeProvider` so the standalone
  // configurator wizard, which renders outside this layout, adopts it too.)

  // Publish the content card's live viewport rect as CSS vars on :root, so the
  // area's portalled overlays (modals, toasts — all fixed-positioned on <body>)
  // can anchor to the content card instead of the off-centre viewport. The body
  // is off-centre on desktop (side menu 84px ≠ Lumio 380px, inside a max-width
  // centred row), and the offset varies by breakpoint, so we MEASURE rather than
  // re-derive it in CSS. See theme/contentAreaOverlay.ts for the consumers.
  const contentRef = React.useRef<HTMLElement>(null);
  React.useLayoutEffect(() => {
    const el = contentRef.current;
    const root = document.documentElement;
    if (!el) return undefined;
    const publish = (): void => {
      const r = el.getBoundingClientRect();
      root.style.setProperty('--content-left', `${r.left}px`);
      root.style.setProperty('--content-top', `${r.top}px`);
      root.style.setProperty('--content-width', `${r.width}px`);
      root.style.setProperty('--content-height', `${r.height}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      // Drop the vars so anything mounted after this layout unmounts (the
      // full-bleed configurator, auth screens) falls back to the full viewport.
      root.style.removeProperty('--content-left');
      root.style.removeProperty('--content-top');
      root.style.removeProperty('--content-width');
      root.style.removeProperty('--content-height');
    };
    // Re-run once auth resolves, when <Content> actually mounts (the ref is null
    // on the unauthenticated early-return below, so the first pass would no-op).
  }, [authenticated]);

  // Lumio: docked in a column on wide desktop (≥1280px), but on the mid-desktop
  // range the column is hidden and the top-bar trigger opens it as a drawer.
  const [lumioOpen, setLumioOpen] = React.useState(false);
  // The drawer ONLY exists inside its band (the trigger that opens it lives there
  // too). Outside it — wide desktop (the column shows) or mobile (the bottom nav
  // owns Lumio) — the drawer is unmounted, so leaving the band makes it vanish at
  // once instead of sliding out over the docked column or floating over the mobile
  // chrome, and we reset state so re-entering the band never re-pops it open.
  const inDrawerBand = useMediaQuery(LUMIO_DRAWER_RANGE);
  // Wide desktop: the docked column can be collapsed from its header (the close disc) and reopened from the top-bar
  // trigger; remembered across visits. Collapsed by default: it only shows once the owner opens it.
  const [dockCollapsed, setDockCollapsed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem('ows.lumio.dockCollapsed') !== '0';
    } catch {
      return true;
    }
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem('ows.lumio.dockCollapsed', dockCollapsed ? '1' : '0');
    } catch {
      /* storage unavailable: keep the in-memory state */
    }
  }, [dockCollapsed]);
  const collapseDock = React.useCallback(() => setDockCollapsed(true), []);
  const openLumio = React.useCallback(() => setLumioOpen(true), []);
  const closeLumio = React.useCallback(() => setLumioOpen(false), []);
  React.useEffect(() => {
    if (!inDrawerBand) setLumioOpen(false);
  }, [inDrawerBand]);

  // Mobile (≤899px): the bottom-nav orb opens Lumio as a full-screen "morph"
  // overlay instead of a column/drawer. Mounted only on mobile so it never floats
  // over the desktop chrome; leaving the breakpoint closes + unmounts it.
  const isMobile = useMediaQuery(MOBILE_MQ);
  const [mobileLumioOpen, setMobileLumioOpen] = React.useState(false);
  // The bottom-bar orb is hidden while Lumio is open AND through the close
  // animation, so the nav orb and the overlay orb sliding home are never both on
  // screen. It's revealed only once the overlay has fully left (after closeMs);
  // the reveal is instant (see MobileNav PageShadow) because the overlay orb lands
  // exactly on the nav orb's spot, so the handoff is seamless.
  const [navHidden, setNavHidden] = React.useState(false);
  const navRevealTimer = React.useRef<number | null>(null);
  const clearNavReveal = React.useCallback(() => {
    if (navRevealTimer.current !== null) {
      window.clearTimeout(navRevealTimer.current);
      navRevealTimer.current = null;
    }
  }, []);
  const openMobileLumio = React.useCallback(() => {
    clearNavReveal();
    setNavHidden(true);
    setMobileLumioOpen(true);
  }, [clearNavReveal]);
  const closeMobileLumio = React.useCallback(() => {
    setMobileLumioOpen(false);
    clearNavReveal();
    // Reveal (animate in) the bar once the overlay orb has slid home; the overlay
    // stays on top (occluding the nav orb) until it fully hides at closeMs, so the
    // orb hands off seamlessly while the rest of the bar fades/slides back in.
    navRevealTimer.current = window.setTimeout(() => {
      setNavHidden(false);
      navRevealTimer.current = null;
    }, LUMIO_MOBILE_ANIM.orbSlideMs);
  }, [clearNavReveal]);
  React.useEffect(() => clearNavReveal, [clearNavReveal]);
  React.useEffect(() => {
    if (!isMobile) {
      setMobileLumioOpen(false);
      clearNavReveal();
      setNavHidden(false);
    }
  }, [isMobile, clearNavReveal]);

  // FTUX - Getting Started: the moment it takes the screen (Lumio's hello, a piece's pop-up), Lumio's chat steps aside
  // in whichever surface it is open in (the docked column, the drawer, the phone's overlay). Only then: reopened
  // mid-flow (a tester's QA strip), it stays open.
  const ftuxWasBusy = React.useRef(false);
  React.useEffect(() => {
    const starts = ftuxBusy && !ftuxWasBusy.current;
    ftuxWasBusy.current = ftuxBusy;
    if (!starts) return;
    setDockCollapsed(true);
    setLumioOpen(false);
    if (mobileLumioOpen) closeMobileLumio();
  }, [ftuxBusy, mobileLumioOpen, closeMobileLumio]);
  // The menu map opening (after the hello, or a tester's jump to it) on a phone: Lumio's overlay steps aside too, as it
  // hides the bottom bar the map tells.
  const ftuxMapOn = useAppSelector((state) => selectFtuxMapOn(state, location.pathname === ROUTE.home()));
  const ftuxWasMap = React.useRef(false);
  React.useEffect(() => {
    const starts = ftuxMapOn && !ftuxWasMap.current;
    ftuxWasMap.current = ftuxMapOn;
    if (starts && mobileLumioOpen) closeMobileLumio();
  }, [ftuxMapOn, mobileLumioOpen, closeMobileLumio]);

  // "Tell me more" bus: a page (e.g. product detail) asks Lumio a question; we
  // seed the prompt for the active surface to send and open whichever surface
  // owns Lumio at this breakpoint. The seed carries a nonce so the same prompt
  // can be re-asked and so the active surface sends it exactly once; the
  // consuming surface clears it (consumeSeed), so a later breakpoint switch never
  // replays a stale prompt into a freshly-mounted surface.
  const [lumioSeed, setLumioSeed] = React.useState<LumioSeed | null>(null);
  const [consumedNonce, setConsumedNonce] = React.useState(0);
  const seedNonce = React.useRef(0);
  const consumeSeed = React.useCallback((nonce: number) => {
    setConsumedNonce((prev) => Math.max(prev, nonce));
    setLumioSeed(null);
  }, []);
  const askLumio = React.useCallback(
    (prompt: string) => {
      const text = prompt.trim();
      if (!text) return;
      seedNonce.current += 1;
      setLumioSeed({ text, nonce: seedNonce.current });
      // Open the surface that owns Lumio at this breakpoint. On wide desktop the
      // docked column is always visible, so there is nothing to open.
      if (isMobile) openMobileLumio();
      else if (inDrawerBand) openLumio();
      else setDockCollapsed(false);
    },
    [isMobile, inDrawerBand, openMobileLumio, openLumio],
  );
  // A support agent signed in as this owner: Lumio keeps the FTUX tests off (777ftuxqa refused, no QA strip).
  const impersonating = Boolean(impersonator);
  const lumioControl = React.useMemo<LumioControl>(
    () => ({ askLumio, seed: lumioSeed, consumedNonce, consumeSeed, impersonating }),
    [askLumio, lumioSeed, consumedNonce, consumeSeed, impersonating],
  );

  // Proactive page nudges — evaluated on navigation. Docked into the Lumio panel's
  // briefing (the "Noticed" card) via LumioNudgeContext when a panel is visible,
  // and floated as a toast only when every Lumio surface is closed (see below).
  //
  // Panels report whether they can currently dock one (they can only do it from
  // their briefing, so a started conversation or an open inbox takes the surface
  // away). Without that count the layout would keep asking for nudges nothing can
  // render, and each one would be reported `ignored` on the next navigation,
  // spending the owner's frequency budget on notices they never saw.
  const [dockSurfaces, setDockSurfaces] = React.useState(0);
  const reportSurface = React.useCallback((delta: number) => setDockSurfaces((n) => n + delta), []);

  // The toast floats whenever no Lumio surface is COVERING the screen. On wide
  // desktop the docked column sits beside the content rather than over it, so
  // the toast is available there too: it is the catch-all that makes sure a
  // notice always has somewhere to land.
  //
  // That matters because asking is the only thing that costs anything. The
  // backend spends a nudge when it emits it (nudges.py:742-833) and nothing we
  // report afterwards gives it back, so evaluating without a surface throws away
  // one of three per-session slots. Gating on a surface is correct; the toast is
  // what stops that gate from starving a viewport.
  const nudgeToastFits = !(isMobile && mobileLumioOpen) && !(inDrawerBand && lumioOpen);
  const nudgeSurfaceExists = dockSurfaces > 0 || nudgeToastFits;
  const { nudge, resolve: resolveNudge } = useLumioNudges(authenticated && nudgeSurfaceExists && !ftuxBusy);
  const lumioNudgeBus = React.useMemo(
    () => ({ nudge, resolve: resolveNudge, reportSurface }),
    [nudge, resolveNudge, reportSurface],
  );

  // A panel takes the notice only if it will actually RENDER it. Three things
  // can stop that: no briefing is showing, the briefing declines the id
  // (`pending_decisions` belongs to the Needs-you card), or the notice has no
  // copy to render at all, which the backend documents as its unknown-id case.
  // Miss the last one and such a notice is docked by nobody, hidden from the
  // toast, and left occupying the slot, which stalls every later evaluation.
  const nudgeDocked = dockSurfaces > 0 && isDockableNudge(nudge) && Boolean(nudge && renderNudge(nudge));

  // "Lumio, write me an article" bus: the assistant asks the studio to do
  // something, rather than just answering. The layout owns it because it is the
  // only place above BOTH the Lumio surfaces and the pages, and it already has
  // the store and the router. The blog list is where the work is visible (a
  // generating row for the length of the job), so a request from anywhere else
  // takes the owner there first, then starts writing.
  /*
   * Something is being written for this brand — asked of the SERVICE, so it is
   * true across tabs and devices rather than only in the tab that asked.
   *
   * There used to be a ref beside this, flipped synchronously so two asks in the
   * same tick could not both start. It is gone: it only ever guarded the page it
   * lived on, and the honest answer to "is one already running" now comes from
   * the same place every other surface reads it. What the ref covered and this
   * does not is two asks between a dispatch and its re-render — not a path a
   * person types, and the cost is a second real draft rather than a broken one.
   */
  const writingArticle = useAppSelector((state) =>
    /*
     * Whole-article passes only — the question Lumio is actually asking.
     *
     * The service's rule is one job per ARTICLE, so a hero draw or an SEO pass
     * on some unrelated article says nothing about whether Lumio can write a new
     * one. Counting those made it refuse with "I am still writing the previous
     * article" while it was writing nothing at all, and silently swallow the
     * retry. The list this replaced held Lumio's own jobs, which is the same
     * scope as this.
     */
    Object.values(state.blog.jobs).some((job) => job.status === 'running' && job.kind === 'article'),
  );

  /*
   * Keep the brand-wide answer honest while anything is being written.
   *
   * `writingArticle` above is read from `state.blog.jobs`, and only the blog
   * list and the editor ever refresh that map — so a job that finished while the
   * owner was on Home stayed `running` in the store for the rest of the session,
   * and Lumio answered every request with "I am still writing the previous one".
   * This layout is the one surface always mounted, so it sweeps.
   *
   * It polls only while it believes something IS running, and stops when that
   * resolves — so an idle studio makes no requests, and this never becomes a
   * third permanent poller.
   */
  React.useEffect(() => {
    if (!writingArticle) return undefined;

    return pollWhileVisible(() => void dispatch(blogPollTenantJobs()));
  }, [dispatch, writingArticle]);

  // Buy-credits for the chat surface. The editor and the creatives pages each
  // mount their own sheet; the chat lives HERE, so this layout is where its
  // 402 remediation has to live too.
  const [buyCreditsOpen, setBuyCreditsOpen] = React.useState(false);
  const [buyCreditsSuggested, setBuyCreditsSuggested] = React.useState<number | undefined>(undefined);
  // Who asked for the sheet: the chat's 402 wants nothing after the purchase (the sheet shows its
  // own success screen); the Autopilot email's deep link wants the notice closed and the missed
  // article resumed (`settleAutopilotTopup`).
  const [buyCreditsFor, setBuyCreditsFor] = React.useState<'chat' | 'autopilot'>('chat');
  const openBuyCredits = React.useCallback((suggested?: number, purpose: 'chat' | 'autopilot' = 'chat') => {
    setBuyCreditsSuggested(suggested && suggested > 0 ? suggested : undefined);
    setBuyCreditsFor(purpose);
    setBuyCreditsOpen(true);
  }, []);
  const onBuyCreditsSuccess = React.useCallback(() => {
    if (buyCreditsFor === 'autopilot') void settleAutopilotTopup(dispatch, { walletFresh: true });
  }, [buyCreditsFor, dispatch]);

  // An Autopilot credits notice the owner's balance already covers is closed, and the missed article asked for,
  // whichever way the credits arrived (the header chip, the creatives page, the editor): the service never hears
  // of a top-up, and this layout is the one surface always mounted. No request while no notice is open.
  useAutopilotCreditsSettle();

  /*
   * The Autopilot credits email links to `/account/home?credits=buy`
   * (`docs/auto-generation.md` in spark-blog, "Email deep link"): open the
   * buy-credits sheet this layout already hosts, once, and take the parameter
   * off the URL so a reload or a back-navigation does not reopen it. The sheet
   * is primed with the price the open notice names, which means asking the
   * service first; the email already said the number, so a read that fails
   * still opens the sheet, unprimed. Both wait for the session and the brand:
   * before the auth gate resolves there is nothing to show, and before a brand
   * is displayed there is no tenant to ask (the brand list loads within the
   * same first paint, from Home's own notices and the brand switcher).
   */
  const displayBrandId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const creditsLinkHandled = React.useRef(false);
  React.useEffect(() => {
    if (creditsLinkHandled.current || !authenticated || displayBrandId === undefined) return;
    const params = new URLSearchParams(location.search);
    if (params.get('credits') !== 'buy') return;
    creditsLinkHandled.current = true;
    params.delete('credits');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    void dispatch(blogFetchNotices())
      .unwrap()
      .then((notices) => openBuyCredits(notices.credits?.needed, 'autopilot'))
      .catch(() => openBuyCredits(undefined, 'autopilot'));
  }, [authenticated, displayBrandId, location.search, location.pathname, location.hash, navigate, dispatch, openBuyCredits]);

  const writeArticle = React.useCallback(
    async (topic: string): Promise<LumioArticleResult> => {
      navigate(ROUTE.blog());

      // The draft exists and the job is claimed the moment this answers; the
      // writing carries on in the service. That is what puts a real row in the
      // list straight away instead of a placeholder this tab is holding.
      const { article } = await dispatch(
        // `parts` is fixed by the route, which exists for the whole-article pass
        // and sends it regardless; naming it here keeps the brief honest rather
        // than relying on the client to substitute it.
        blogStartArticleGeneration({ parts: ['article'], brief: topic }),
      )
        .unwrap()
        .catch((err: unknown) => {
          // Not enough credits (the service's 402): this layout owns the
          // remediation — open Buy-credits primed with the shortfall — and the
          // rethrown refusal tells the chat to answer with honest copy instead
          // of its generic error + Retry row (which would retry into the same
          // 402 forever).
          if (blogInsufficientCredits(err)) openBuyCredits(blogNeededCredits(err));
          throw err;
        });

      // The claim charged credits: refresh the header chip so the owner
      // watches the real balance, exactly as every other charged surface does.
      void dispatch(fetchOwnerWallets());

      /*
       * Wait for the SERVICE to finish rather than for a request to answer.
       *
       * The conversation promises a finished article by name ("X is ready"), and
       * the name only exists once the pass has written it — the draft is titled
       * from the owner's own brief until then. So this polls to the end. Unlike
       * the request it replaces, nothing is lost if the owner walks away: the
       * work is a row in the database, and every other surface is watching it.
       *
       * It carries on if the panel closes, and deliberately: the turn that reads
       * "X is ready" belongs to a thread the owner can come back to. It is bound
       * either way — the job carries a 30-minute deadline and the boot sweep
       * fails anything left running, so the loop always meets an ending.
       */
      /*
       * A bound, so this can never become a poller nothing stops.
       *
       * The service gives a job thirty minutes and fails whatever is left
       * running at boot, so a job always ends — but only while it is still
       * READABLE. Logging out or switching brand makes it unreadable from here,
       * and without a bound the loop then polls a foreign tenant every few
       * seconds for the life of the tab, with the turn stuck mid-sentence.
       */
      const deadline = Date.now() + JOB_WAIT_LIMIT_MS;

      for (;;) {
        // Not copy: the chat panel discards a rejection's message and renders
        // its own error + Retry row, so this only has to END the turn.
        if (Date.now() > deadline) throw new Error('the generation outlived this session');

        /*
         * A tick that FAILS is not a generation that failed.
         *
         * This loop makes dozens of these over a pass that takes minutes, so a
         * single 502, a dropped connection or a token refresh used to reject the
         * whole turn — telling the owner the article did not happen while the
         * service was still busy writing it. The next tick is three seconds
         * away; only the service's own verdict ends this.
         */
        const job = await dispatch(blogPollArticleJobs(article.id))
          .unwrap()
          .then((result) => result.job)
          .catch((err: unknown) => {
            // The brand moved under this turn, so the turn is void — not
            // something to retry. Swallowing this is how the loop used to run
            // forever: every tick rejected, every rejection looked transient.
            if (isBrandChanged(err)) throw err;

            return undefined;
          });

        // Rejecting is what puts the panel's ordinary error + Retry row on the
        // turn. The service's own words, so the reason is the real one.
        if (job?.status === 'failed') throw new Error(job.error ?? 'The article could not be written');

        if (job?.status === 'done') break;

        /*
         * Anything else — still running, or an answer this tick could not get —
         * means the outcome is still UNKNOWN, and waiting is the only honest
         * response to that.
         *
         * `break`ing here is how a failed generation used to be announced as a
         * finished one: another surface resolving the ending first made this
         * poll answer with nothing, and nothing read as success. The blog list
         * is always mounted for this (writeArticle navigates there first), so it
         * was a race on every single request, not an edge case.
         */
        await new Promise((resolve) => window.setTimeout(resolve, JOB_POLL_MS));
      }

      /*
       * The article IS written by this point. A list fetch failing now is a
       * stale name, not a failed generation — and reporting it as one made the
       * panel offer Retry, which wrote a second article for the same request.
       */
      const written = await dispatch(blogFetch())
        .unwrap()
        .then(({ articles }) => articles.find((a) => a.id === article.id)?.title)
        .catch(() => undefined);

      return { id: article.id, title: written ?? article.title };
    },
    [dispatch, navigate, openBuyCredits],
  );
  // Lumio offers to write an article to every owner. It used to be withheld
  // while the blog area was unrevealed — the hook treats a null `writeArticle`
  // as "this studio does not do that" — so that Lumio could not write into a
  // section the owner could not open, nor navigate them to a route that
  // answered not-found. The blog is ungated now, so there is nothing to
  // withhold it for; the null arm stays as the hook's own contract.
  const lumioArticle = React.useMemo<LumioArticleControl>(
    () => ({ writeArticle, writing: writingArticle }),
    [writeArticle, writingArticle],
  );

  if (gate !== undefined) return gate;

  return (
    <ImmersiveControlContext.Provider value={immersiveControl}>
      <LumioControlContext.Provider value={lumioControl}>
        <LumioArticleContext.Provider value={lumioArticle}>
          <LumioNudgeContext.Provider value={lumioNudgeBus}>
            {/* Paint the whole document the themed page background while the studio is
              mounted (so the config shell's dark body never shows behind/around the
              chrome). Dark is unchanged; light makes the backdrop off-white. */}
            <StudioBackdrop />
            {/* Immersive (full-screen Advanced Video editor): collapse the layout gutters / content card /
              max-width (desktop only, scoped to `.pg-layout[data-immersive]`) so the in-flow canvas reaches
              the viewport edges. Mounted ONLY while immersive so it can never leak onto another page. */}
            {immersive && <ImmersiveLayout />}
            {/* Mobile: make the document itself the scroller so the browser chrome
              (iOS URL bar) hides on scroll. Only active ≤899px; unmounts with the
              layout, re-pinning the document for the config-driven pages. */}
            <MobileBodyScroll />
            {/* Support-impersonation notice — pinned to the top of the viewport; the chrome
              offsets itself by its measured height (--ows-imp-offset). Only present while
              an admin impersonates. */}
            {impersonator && (
              <ImpersonationBanner
                ref={setBannerNode}
                username={impersonator.username}
                onSignOut={handleImpersonationSignOut}
                signingOut={signingOut}
              />
            )}
            {/* While the full-screen mobile Lumio overlay is open, hide the page chrome
              from assistive tech so the dialog's aria-modal contract holds (the overlay
              renders OUTSIDE Root, so it stays exposed). */}
            <Root aria-hidden={(isMobile && mobileLumioOpen) || undefined} data-immersive={immersive || undefined}>
              <SideMenuCol>
                <SideMenu />
              </SideMenuCol>
              {/* Immersive mode slid the side menu out; the back button that restores it lives in the top bar's
                breadcrumb row (see TopBar), so it lines up with the "· Creatives" label instead of floating. */}
              <Body>
                <TopBar
                  onOpenLumio={
                    isMobile ? openMobileLumio : inDrawerBand ? openLumio : () => setDockCollapsed(false)
                  }
                  lumioOpen={isMobile ? mobileLumioOpen : inDrawerBand ? lumioOpen : !dockCollapsed}
                  showLumioTriggerOnDesktop={dockCollapsed}
                />
                <VerifyEmailBanner />
                <Content ref={contentRef}>{children}</Content>
              </Body>
              {/* Docked Lumio column — hidden on the Creatives surface (it has its own Lumio). */}
              {/* Always mounted (keeps the conversation); collapsing animates it out, see LumioCol. */}
              {(
                <LumioCol $collapsed={dockCollapsed} aria-hidden={dockCollapsed} data-ftux-qa-lift="">
                  {/* Docked panel is the active Lumio surface only on desktop; in the
                    drawer band / on mobile it is hidden, so don't fetch suggestions
                    (the drawer / mobile overlay does that when opened). */}
                  <LumioChat active={!inDrawerBand && !isMobile && !dockCollapsed} onClose={collapseDock} />
                </LumioCol>
              )}

              {buyCreditsOpen ? (
                <BuyCreditsSheet
                  suggestedCredits={buyCreditsSuggested}
                  onClose={() => setBuyCreditsOpen(false)}
                  // The sheet shows its own success screen once the credits land,
                  // and this layout has no toast rail of its own to echo it on.
                  // The Autopilot deep link is the one purpose with work left
                  // to do afterwards (see onBuyCreditsSuccess).
                  onSuccess={onBuyCreditsSuccess}
                />
              ) : null}
            </Root>
            <MobileNav lumioActive={navHidden} showcase={newtonShowcase} />
            {inDrawerBand && <LumioDrawer open={lumioOpen} onClose={closeLumio} />}
            {isMobile && <LumioMobile open={mobileLumioOpen} onClose={closeMobileLumio} />}
            {/* Proactive nudge — the catch-all surface. A panel docks it when its
                briefing is showing and will render it; everything else floats here,
                including on wide desktop, where it steps left of the docked column.
                Without that fallback the surface gate would starve that viewport,
                whose briefing disappears for the session on the owner's first
                question. Its chip opens Lumio and asks — a passive notice becomes a
                real turn. */}
            {nudge && !nudgeDocked && nudgeToastFits && !ftuxBusy && (
              <NudgeToast
                nudge={nudge}
                onAct={(message) => {
                  resolveNudge('clicked');
                  askLumio(message);
                }}
                onDismiss={() => resolveNudge('dismissed')}
                onTimeout={() => resolveNudge('ignored')}
              />
            )}
            {/* FTUX - Getting Started: its own chunk, loaded only while the flag is on; never while a support agent is
                signed in as the owner (no events, no preference writes). */}
            {ftuxOn && !impersonator ? <FtuxRootLazy /> : null}
          </LumioNudgeContext.Provider>
        </LumioArticleContext.Provider>
      </LumioControlContext.Provider>
    </ImmersiveControlContext.Provider>
  );
};

export default MainLayout;
